import React, { useMemo } from 'react';
type DailyData = { pnl: number };
import { fmtMoney } from '../../utils/formatters';
import { calculateWinRateRatio } from '../../lib/trades/winRate';
import { computeTradeLevelStats } from '../../lib/trades/tradeLevelStats';

interface WinVsLossPerformanceWidgetProps {
  dailyData: DailyData[];
  /** Authoritative trade records; when provided, averages use individual trades. */
  trades?: any[];
}

const safeRatio = (numerator: number, denominator: number) => {
  if (!isFinite(numerator) || !isFinite(denominator) || denominator === 0) return 0;
  return numerator / denominator;
};

export const WinVsLossPerformanceWidget: React.FC<WinVsLossPerformanceWidgetProps> = ({ dailyData, trades }) => {
  const stats = useMemo(() => {
    if (Array.isArray(trades) && trades.length > 0) {
      const tradeStats = computeTradeLevelStats(trades);
      const avgWin = tradeStats.avgWinningTrade;
      const avgLoss = Math.abs(tradeStats.avgLosingTrade);
      const winRate = tradeStats.winRate;
      const lossRate = tradeStats.wins + tradeStats.losses > 0 ? tradeStats.losses / (tradeStats.wins + tradeStats.losses) : 0;
      const edge = avgLoss === 0 ? null : safeRatio(avgWin, avgLoss);

      const magnitudeSum = Math.max(avgWin + avgLoss, 1);
      const winPortion = avgWin > 0 ? avgWin / magnitudeSum : 0;
      const lossPortion = avgLoss > 0 ? avgLoss / magnitudeSum : 0;

      return { avgWin, avgLoss, winRate, lossRate, edge, winPortion, lossPortion };
    }

    const wins = dailyData.filter(d => d.pnl > 0);
    const losses = dailyData.filter(d => d.pnl < 0);

    const avgWin = wins.length ? wins.reduce((acc, d) => acc + d.pnl, 0) / wins.length : 0;
    const avgLoss = losses.length ? losses.reduce((acc, d) => acc + d.pnl, 0) / losses.length : 0; // negative
    const winRate = calculateWinRateRatio(wins.length, losses.length);
    const rateDenominator = wins.length + losses.length;
    const lossRate = rateDenominator > 0 ? losses.length / rateDenominator : 0;
    const edge = avgLoss === 0 ? null : safeRatio(avgWin, Math.abs(avgLoss));

    const magnitudeSum = Math.max(avgWin + Math.abs(avgLoss), 1);
    const winPortion = avgWin > 0 ? avgWin / magnitudeSum : 0;
    const lossPortion = Math.abs(avgLoss) > 0 ? Math.abs(avgLoss) / magnitudeSum : 0;

    return {
      avgWin,
      avgLoss: Math.abs(avgLoss),
      winRate,
      lossRate,
      edge,
      winPortion,
      lossPortion,
    };
  }, [dailyData, trades]);

  const { avgWin, avgLoss, winRate, lossRate, edge, winPortion, lossPortion } = stats;
  const payoffRatioDisplay = edge != null && Number.isFinite(edge) && edge >= 0
    ? edge.toFixed(2)
    : 'N/A';

  return (
    <div className="mt-[2px] relative h-full w-full rounded-[26px] flex flex-col text-white overflow-visible">

      <div className="grid grid-cols-1 sm:grid-cols-2 items-center mt-[10px]">
        <div className="flex flex-col gap-0">
          <div className="text-xs sm:text-sm font-semibold uppercase tracking-[0.12em] text-emerald-600 flex items-center">
            <span className="truncate text-[12px]">Avg Win~</span>
            <span className="text-[12px] text-emerald-600/80">{(winRate * 100).toFixed(0)}%</span>
          </div>
          <div className="text-1xl sm:text-2xl md:text-3xl font-bold text-emerald-600 leading-tight mt-[0px]">
            {avgWin > 0 ? fmtMoney(avgWin) : '$0'}
          </div>
        </div>

          <div className="flex flex-col text-right">
          <div className="text-xs sm:text-sm font-semibold uppercase tracking-[0.12em] text-red-400 flex items-center justify-end">
            <span className="text-[12px] text-red-400/80">{(lossRate * 100).toFixed(0)}%</span>
            <span className="truncate text-[12px]">~Avg Loss</span>
          </div>
          <div className="text-1xl sm:text-2xl md:text-3xl font-bold text-red-500 leading-tight mt-[0px]">
            {avgLoss > 0 ? fmtMoney(-avgLoss) : '$0'}
          </div>
        </div>
      </div>

      {/* Floating payoff-ratio badge at the top (overlay, does not affect layout) */}
      <div className="absolute left-1/2 -top-[14px] z-20 -translate-x-1/2 pointer-events-none">
          <div
            data-testid="average-win-loss-edge"
            role="img"
            aria-label={`Payoff Ratio ${payoffRatioDisplay}`}
            tabIndex={0}
            title="Payoff ratio = average winning trade divided by the absolute average losing trade. It is not an R-multiple."
            className="pointer-events-auto inline-flex min-w-[104px] items-center justify-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-1.5 py-0 text-[10px] font-semibold text-blue-700 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-blue-400/40 dark:bg-blue-500/10 dark:text-blue-200"
          >
            <span className="text-[9px] text-zinc-800 dark:text-blue-200">Payoff Ratio</span>
            <span className="text-xs font-bold text-blue-700 dark:text-blue-100">{payoffRatioDisplay}</span>
          </div>
        </div>

      <div className="relative mt-[4px] h-2 sm:h-2.5 rounded-full bg-[#0f1118] border border-zinc-800/80 overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-emerald-600 via-emerald-600 to-emerald-600"
          style={{ width: `${winPortion * 100}%` }}
        />
        <div
          className="absolute right-0 top-0 h-full bg-gradient-to-r from-red-500 via-red-500 to-red-700"
          style={{ width: `${lossPortion * 100}%` }}
        />
        <div className="absolute inset-y-0 left-1/2 w-0.5 bg-black/60" />
      </div>
    </div>
  );
};

// Backwards-compatible alias expected by the widget registry
export const AvgWinLossWidget = WinVsLossPerformanceWidget;
